import type { ColumnOrderState, TableColumnPinningState } from '../tanstack-table'

export interface TableColumnLayout {
  columnOrder: ColumnOrderState
  pinning: TableColumnPinningState
}

export interface TableColumnLayoutMove {
  activeId: string
  isSearchActive: boolean
  overId?: string
  placement?: TableColumnPlacement
  targetRegion: TableColumnRegion
}

export type TableColumnPlacement = 'after' | 'before'
export type TableColumnRegion = 'center' | 'end' | 'start'

type TableColumnPinningInput = Partial<TableColumnPinningState>

/** Whether two column id lists hold the same ids in the same order; two missing lists match. */
export const sameLayout = (first: string[] | undefined, second: string[] | undefined) => {
  if (!first || !second) return first === second
  return first.length === second.length && first.every((id, index) => id === second[index])
}

/** Applies one table-settings move while preserving three-region layout invariants. */
export const moveTableColumnLayout = ({
  columnOrder,
  leafColumnIds,
  lockedPinning,
  move,
  pinning,
}: {
  columnOrder: ColumnOrderState
  leafColumnIds: string[]
  lockedPinning?: TableColumnPinningInput
  move: TableColumnLayoutMove
  pinning: TableColumnPinningInput
}): TableColumnLayout => {
  const validIds = uniqueIds(leafColumnIds)
  const validIdSet = new Set(validIds)
  const normalizedOrder = normalizeOrder(columnOrder, validIds)
  const normalizedLocks = normalizePinning(lockedPinning, validIdSet)
  const normalizedPinning = enforceLocks(normalizePinning(pinning, validIdSet), normalizedLocks)

  if (!validIdSet.has(move.activeId) || isLocked(move.activeId, normalizedLocks)) {
    return { columnOrder: normalizedOrder, pinning: normalizedPinning }
  }

  if (move.overId === move.activeId) {
    return { columnOrder: normalizedOrder, pinning: normalizedPinning }
  }

  const sourceRegion = getRegion(move.activeId, normalizedPinning)
  if (move.isSearchActive && sourceRegion === move.targetRegion) {
    return { columnOrder: normalizedOrder, pinning: normalizedPinning }
  }

  if (sourceRegion === move.targetRegion) {
    if (sourceRegion === 'center') {
      return {
        columnOrder: moveInOrder(
          normalizedOrder,
          move.activeId,
          move.overId,
          move.placement,
          normalizedPinning,
          validIdSet,
          true,
        ),
        pinning: normalizedPinning,
      }
    }

    return {
      columnOrder: normalizedOrder,
      pinning: moveInPinning(
        normalizedPinning,
        sourceRegion,
        move.activeId,
        move.overId,
        move.placement,
        normalizedLocks,
      ),
    }
  }

  const nextPinning = removeFromPinning(normalizedPinning, move.activeId)
  if (move.targetRegion === 'center') {
    return {
      columnOrder: moveInOrder(
        normalizedOrder,
        move.activeId,
        move.isSearchActive ? undefined : move.overId,
        move.placement,
        nextPinning,
        validIdSet,
        false,
      ),
      pinning: enforceLocks(nextPinning, normalizedLocks),
    }
  }

  const targetIds = nextPinning[move.targetRegion]
  const insertAt = move.isSearchActive
    ? targetIds.length
    : insertionIndex(targetIds, move.overId, move.placement, -1)
  const nextTargetIds = [...targetIds]
  nextTargetIds.splice(insertAt, 0, move.activeId)

  return {
    columnOrder: normalizedOrder,
    pinning: enforceLocks({ ...nextPinning, [move.targetRegion]: nextTargetIds }, normalizedLocks),
  }
}

const uniqueIds = (ids: string[]): string[] => {
  const seen = new Set<string>()
  return ids.filter(id => {
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
}

const normalizeOrder = (order: string[], validIds: string[]): string[] => {
  const validIdSet = new Set(validIds)
  const seenKnownIds = new Set<string>()
  const result = order.filter(id => {
    if (!validIdSet.has(id)) return true
    if (seenKnownIds.has(id)) return false
    seenKnownIds.add(id)
    return true
  })

  return [...result, ...validIds.filter(id => !seenKnownIds.has(id))]
}

const normalizePinning = (
  pinning: TableColumnPinningInput | undefined,
  validIds: Set<string>,
): TableColumnPinningState => {
  const seen = new Set<string>()
  const collect = (ids: string[] | undefined) =>
    (ids ?? []).filter(id => {
      if (!validIds.has(id) || seen.has(id)) return false
      seen.add(id)
      return true
    })

  const start = collect(pinning?.start)
  const end = collect(pinning?.end)
  return { end, start }
}

const enforceLocks = (
  pinning: TableColumnPinningState,
  lockedPinning: TableColumnPinningState,
): TableColumnPinningState => {
  const lockedStart = lockedPinning.start ?? []
  const lockedEnd = keepActionsLast(lockedPinning.end ?? [])
  const lockedIds = new Set([...lockedStart, ...lockedEnd])

  return {
    end: [...(pinning.end ?? []).filter(id => !lockedIds.has(id)), ...lockedEnd],
    start: [...lockedStart, ...(pinning.start ?? []).filter(id => !lockedIds.has(id))],
  }
}

const keepActionsLast = (ids: string[]): string[] =>
  ids.includes('actions') ? [...ids.filter(id => id !== 'actions'), 'actions'] : ids

const isLocked = (id: string, lockedPinning: TableColumnPinningState) =>
  lockedPinning.start?.includes(id) || lockedPinning.end?.includes(id)

const getRegion = (id: string, pinning: TableColumnPinningState): TableColumnRegion => {
  if (pinning.start?.includes(id)) return 'start'
  if (pinning.end?.includes(id)) return 'end'
  return 'center'
}

const removeFromPinning = (
  pinning: TableColumnPinningState,
  id: string,
): TableColumnPinningState => ({
  end: (pinning.end ?? []).filter(pinnedId => pinnedId !== id),
  start: (pinning.start ?? []).filter(pinnedId => pinnedId !== id),
})

const moveInOrder = (
  order: string[],
  activeId: string,
  overId: string | undefined,
  placement: TableColumnPlacement | undefined,
  pinning: TableColumnPinningState,
  validIds: Set<string>,
  useSameRegionPlacement: boolean,
): string[] => {
  const activeIndex = useSameRegionPlacement ? order.indexOf(activeId) : -1
  const withoutActive = order.filter(id => id !== activeId)
  const centerIds = new Set(
    withoutActive.filter(
      id => validIds.has(id) && !pinning.start?.includes(id) && !pinning.end?.includes(id),
    ),
  )
  const insertAt =
    overId && centerIds.has(overId)
      ? insertionIndex(withoutActive, overId, placement, activeIndex)
      : withoutActive.length
  const nextOrder = [...withoutActive]
  nextOrder.splice(insertAt, 0, activeId)
  return nextOrder
}

const moveInPinning = (
  pinning: TableColumnPinningState,
  region: Exclude<TableColumnRegion, 'center'>,
  activeId: string,
  overId: string | undefined,
  placement: TableColumnPlacement | undefined,
  lockedPinning: TableColumnPinningState,
): TableColumnPinningState => {
  const ids = pinning[region] ?? []
  const withoutActive = ids.filter(id => id !== activeId)
  const nextIds = [...withoutActive]
  nextIds.splice(
    insertionIndex(withoutActive, overId, placement, ids.indexOf(activeId)),
    0,
    activeId,
  )
  return enforceLocks({ ...pinning, [region]: nextIds }, lockedPinning)
}

const insertionIndex = (
  ids: string[],
  overId: string | undefined,
  placement: TableColumnPlacement | undefined,
  activeIndex: number,
): number => {
  const overIndex = overId ? ids.indexOf(overId) : -1
  if (overIndex === -1) return ids.length

  const resolvedPlacement =
    placement ?? (activeIndex !== -1 && activeIndex <= overIndex ? 'after' : 'before')
  return resolvedPlacement === 'after' ? overIndex + 1 : overIndex
}
